<template>
  <div ref="annotateContainer" id="annotate" tabindex="0" v-on:keydown="handleKeyDown">
    <annotate-tools
      :selected-color.sync="selectedColor"
      :selected-thickness.sync="selectedThickness"
      :selected-font-size.sync="selectedFontSize"
      :selected-tool.sync="selectedTool"
      :outline-text.sync="outlineText"
      :free-user="freeUser"
      :dirty.sync="dirty"
      :share-url="shareUrl"
      :tools="tools"
      :uploading="uploading"
      >
    </annotate-tools>
    <annotate-canvas
      :selected-color="selectedColor"
      :selected-thickness="selectedThickness"
      :selected-font-size="selectedFontSize"
      :selected-tool="selectedTool"
      :outline-text="outlineText"
      :dirty.sync="dirty"
      :uploading.sync="uploading"
      >
    ></annotate-canvas>
  </div>
</template>

<script>
import Vue from 'vue/dist/vue.esm.js';
import track from '@/src/tracking';
import AnnotateTools from './annotate_tools';
import AnnotateCanvas from './annotate_canvas';
import { mapState } from 'vuex';
import EventBus from '../event_bus';

export default {
  data: function () {
    return {
      selectedColor: localStorage.getItem("annotate:selectedColor") || "#FF6191",
      selectedTool: localStorage.getItem("annotate:selectedTool") || "arrow",
      selectedThickness: Math.floor(localStorage.getItem("annotate:selectedThickness")) || 4,
      selectedFontSize: Math.floor(localStorage.getItem("annotate:selectedFontSize")) || 20,
      outlineText: true,
      dirty: false,
      uploading: false,
      tools: [
        {
          type: 'arrow',
          shortcut: 'A',
          title: 'Arrow (A)'
        },
        {
          type: 'line',
          shortcut: 'L',
          title: 'Line (L)'
        },
        {
          type: 'draw',
          shortcut: 'P',
          title: 'Draw (P)'
        },
        {
          type: 'highlighter',
          shortcut: 'H',
          title: 'Highlighter (H)'
        },
        {
          type: 'ellipse',
          shortcut: 'O',
          title: 'Circle (O)'
        },
        {
          type: 'rect',
          shortcut: 'R',
          title: 'Rectangle (R)'
        },
        {
          type: 'number',
          shortcut: 'N',
          title: 'Number (N)'
        },
        {
          type: 'crop',
          shortcut: 'C',
          title: 'Crop (C)'
        },
        {
          type: 'pixelate',
          shortcut: 'B',
          title: 'Blur (B)'
        },
        {
          type: 'text',
          shortcut: 'T',
          title: 'Text (T)'
        },
      ]
    }
  },
  computed: {
    ...mapState({
      imageUrl: state => state.item.links.content_url,
      freeUser: state => state.item.relationships.organization.meta.free,
      shareUrl: state => state.item.links.share_url
    })
  },
  mounted(){
    const vm = this;
    vm.$refs.annotateContainer.addEventListener("wheel", function(e){
      if (e.ctrlKey) { e.preventDefault(); }
    });
    vm.trackAnnotateOpen();
  },
  activated: function() {
    Vue.nextTick(() => {
      this.$refs.annotateContainer.focus();
    });
  },
  components: { AnnotateTools, AnnotateCanvas },
  methods: {
    trackAnnotateOpen: function() {
      const properties = {
        item_id: this.$route.params.slug,
        user_agent: window.navigator.userAgent
      }
      track('web_annotation_viewed', properties)
    },
    resetTools: function() {
      // as we're using <keep-alive> to prevent image from downloading everytime, we need to reset state
      const vm = this;
      vm.selectedTool = "arrow"
      vm.selectedColor = "#FF6191"
      vm.selectedThickness = 4
      vm.selectedFontSize = 40
      vm.outlineText = true
    },
    handleKeyDown: function(e) {
      e.stopPropagation();
      const vm = this;
      const { altKey, ctrlKey, key, keyCode, metaKey, shiftKey} = e;
      const keyUpper = key.toUpperCase();

      // Save when `Ctrl/Cmd + S` is clicked
      if (ctrlKey || metaKey) {
        switch (keyUpper) {
          case 'S':
            e.preventDefault();
            navigator.clipboard.writeText(vm.shareUrl);
            EventBus.$emit('annotate:upload');
            break;

          case 'Z':
            EventBus.$emit('annotate:undo');
            break;

          case 'Y':
            EventBus.$emit('annotate:redo');
            break;

          default:
            break;
        }
      }

      // if any of `Ctrl`, `Shift` or `Alt` is clicked, just ignore
      if (altKey || ctrlKey || metaKey || shiftKey) {
        return;
      }

      // `Delete` or `Backspace` deletes selected shape
      if (keyCode == 46 || keyCode == 8) {
        EventBus.$emit('annotate:delete');
      }

      const targetTool = this.tools.find(tool => tool.shortcut === keyUpper);
      if (targetTool) {
        vm.selectedTool = targetTool.type;
      }
    },
  },
  watch: {
    uploading: function(newValue, _){
      if(newValue) {
        window.onbeforeunload = function(){
          return "Changes are still uploading"
        };
      }else {
        window.onbeforeunload = null;
      }
    },
    dirty: function(newValue, _){
      if(newValue) {
        window.onbeforeunload = function(){
          return "You have unsaved changes"
        };
      }else {
        window.onbeforeunload = null;
      }
    },
  }
}
</script>

<style scoped>
  p {
    font-size: 2em;
    text-align: center;
  }

  #annotate {
    display: flex;
    height: 100vh;
    max-height: 100vh;
    flex-direction: column;
    outline: none;
  }
</style>
